import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import Checkbox from "antd/lib/checkbox";
import message from "antd/lib/message";
import Radio from "antd/lib/radio";
import Form from "antd/lib/form";
import React, { useContext, useState } from "react";
import { useMutation } from "react-query";
import { createApiClient } from "apiClient";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import {
  PatientContext,
  PatientContextState
} from "contextApis/patientContext";
import { UserContext, UserContextState } from "contextApis/userContext";
import { Platform, UserRoles } from "constants/roles";
import { FormSubmitButton } from "pages/symptoms/surveyForms/FormSubmitButton";
import { FormClearButton } from "pages/symptoms/surveyForms/FormClearButton";
import {
  SurveyPage,
  SurveyContent,
  SurveyHeader,
  SurveyNoticeCard,
  SurveyQuestionCard,
  AnswerGrid,
  AnswerList,
  AnswerOption,
  SurveyFooter,
  SurveyProgressBar,
  FooterActions,
  FormItemStyle
} from "pages/symptoms/surveyForms/surveyFormComponents";

interface lightheadednessFormProps {
  setShowSurveyForm(show: boolean): void;
  selectedItems: string[];
  goToNextFormOrReturn(): void;
}

export const LightheadednessForm: React.FC<lightheadednessFormProps> = ({
  setShowSurveyForm,
  selectedItems,
  goToNextFormOrReturn
}) => {
  const params = new URLSearchParams(window.location.search);
  const platform = params.get("platform");
  const [currentPage, setCurrentPage] = useState(1);
  const [showSubmit, setShowSubmit] = useState(false);

  const [form] = Form.useForm();

  const userContext = useContext<UserContextState>(UserContext);
  const patientContext = useContext<PatientContextState>(PatientContext);

  const userRole = sessionStorage.getItem("userRole");

  const {
    isLoading: isLoadingLightheadednessMutation,
    mutate: submitLightheadednessForm
  } = useMutation<string>(
    "add-bill",
    async () => {
      return await createApiClient().addLightheadednessFormData({
        patient_internal_id:
          userRole === UserRoles.PATIENT && userContext.userData
            ? userContext.userData?.internal_id
            : patientContext?.patientData?.id || "",
        lightheadedness: localStorage.getItem("hasHadLightheadedness") || "",
        level: localStorage.getItem("describeYourDizziness") || "",
        frequency: localStorage.getItem("frequencyOfLightheadedness") || "",
        submitted_by: `${userContext.userData?.firstName} ${userContext.userData?.lastName}`
      });
    },
    {
      onSuccess: () => {
        message.success("Form Submitted Successfully");
        goToNextFormOrReturn();
      },
      onError: () => {
        message.error({
          content: (
            <span>
              There was a problem submitting this form, resubmit or{" "}
              <a onClick={() => goToNextFormOrReturn()}>
                click here to continue to the next survey
              </a>
            </span>
          ),
          style: {
            bottom: "10px",
            position: "fixed",
            left: "30%"
          },
          duration: 5
        });
      }
    }
  );

  const onFinish = () => {
    //Values to be passed on to API from session storage
    if (localStorage.getItem("hasHadLightheadedness") === "No") {
      form.resetFields([
        "Frequency of lightheadedness",
        "Describe your dizziness"
      ]);
      localStorage.setItem("frequencyOfLightheadedness", "");
      localStorage.setItem("describeYourDizziness", "");
      submitLightheadednessForm();
    } else if (currentPage === 1) {
      setCurrentPage(2);
    } else {
      submitLightheadednessForm();
    }
  };

  const onClear = () => {
    if (currentPage === 1) {
      form.resetFields(["Any lightheadedness in last month"]);
    } else {
      form.resetFields([
        "Frequency of lightheadedness",
        "Describe your dizziness"
      ]);
    }
  };

  return (
    <SurveyPage>
      <div
        style={{
          marginBottom: "15px",
          display: platform === Platform.MOBILE ? "none" : "block"
        }}
      >
        <AppButton
          type={ButtonType.Primary}
          onClick={() => setShowSurveyForm(false)}
          buttonContent={
            <>
              <ArrowLeftOutlined /> Back
            </>
          }
        />
      </div>

      <SurveyContent>
        <Form
          name="lightheadedness"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          style={{ width: "100%" }}
        >
          <SurveyHeader title={`Survey - ${selectedItems[0]}`} />

          {currentPage === 1 && (
            <>
              <SurveyNoticeCard />

              <SurveyQuestionCard
                title="Have you had any lightheadedness or dizziness in the last one month?"
                required
              >
                <FormItemStyle
                  name="Any lightheadedness in last month"
                  label=""
                  rules={[{ required: true }]}
                  initialValue=""
                >
                  <Radio.Group
                    value={localStorage.getItem("hasHadLightheadedness")}
                    onChange={e => {
                      if (e.target.value === "No") {
                        setShowSubmit(true);
                      } else {
                        setShowSubmit(false);
                      }
                      localStorage.setItem(
                        "hasHadLightheadedness",
                        e.target.value
                      );
                    }}
                    style={{ width: "100%" }}
                  >
                    <AnswerGrid>
                      <AnswerOption>
                        <Radio value="Yes">Yes</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="No">No</Radio>
                      </AnswerOption>
                    </AnswerGrid>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>
            </>
          )}

          {currentPage === 2 && (
            <>
              <SurveyQuestionCard
                title="How often have you had lightheadedness or dizziness in the last one month?"
                required
              >
                <FormItemStyle
                  name="Frequency of lightheadedness"
                  label=""
                  rules={[{ required: true }]}
                  initialValue=""
                >
                  <Radio.Group
                    value={localStorage.getItem("frequencyOfLightheadedness")}
                    onChange={e =>
                      localStorage.setItem(
                        "frequencyOfLightheadedness",
                        e.target.value
                      )
                    }
                    style={{ width: "100%" }}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Radio value="Rarely (1 - 3 times)">
                          Rarely (1 - 3 times)
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Occasionally (4 - 7 times)">
                          Occasionally (4 - 7 times)
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Frequently (8 - 15 times)">
                          Frequently (8 - 15 times)
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Nearly everyday">Nearly everyday</Radio>
                      </AnswerOption>
                    </AnswerList>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>

              <SurveyQuestionCard title="Describe your dizziness" required>
                <FormItemStyle
                  name="Describe your dizziness"
                  label=""
                  rules={[{ required: true }]}
                  initialValue=""
                >
                  <Checkbox.Group
                    style={{ width: "100%" }}
                    onChange={e =>
                      localStorage.setItem(
                        "describeYourDizziness",
                        e.toString()
                      )
                    }
                    value={sessionStorage
                      .getItem("describeYourDizziness")
                      ?.split(/,/)}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Checkbox value="Feeling of spinning or movement">
                          Feeling of spinning or movement
                        </Checkbox>
                      </AnswerOption>
                      <AnswerOption>
                        <Checkbox value="Unsteadiness getting up or walking">
                          Unsteadiness getting up or walking
                        </Checkbox>
                      </AnswerOption>
                      <AnswerOption>
                        <Checkbox value="Feeling distant or foggy">
                          Feeling distant or foggy
                        </Checkbox>
                      </AnswerOption>
                      <AnswerOption>
                        <Checkbox value="Vision impairment/greying out">
                          Vision impairment/greying out
                        </Checkbox>
                      </AnswerOption>
                      <AnswerOption>
                        <Checkbox value="Near passing out">
                          Near passing out
                        </Checkbox>
                      </AnswerOption>
                      <AnswerOption>
                        <Checkbox value="Loss of consciousness">
                          Loss of consciousness
                        </Checkbox>
                      </AnswerOption>
                    </AnswerList>
                  </Checkbox.Group>
                </FormItemStyle>
              </SurveyQuestionCard>
            </>
          )}

          <SurveyFooter>
            <SurveyProgressBar currentPage={currentPage} totalPages={2} />

            <FooterActions>
              <Form.Item>
                <FormClearButton onClear={onClear} />
              </Form.Item>

              {currentPage !== 1 && (
                <AppButton
                  type={ButtonType.Primary}
                  buttonContent={
                    <>
                      <ArrowLeftOutlined /> Back
                    </>
                  }
                  onClick={() => setCurrentPage(1)}
                  style={{ width: "105px" }}
                />
              )}

              <Form.Item>
                <FormSubmitButton
                  type={ButtonType.Primary}
                  buttonContent={
                    currentPage !== 1 || showSubmit ? (
                      "Submit"
                    ) : (
                      <>
                        Next <ArrowRightOutlined />
                      </>
                    )
                  }
                  htmlType="submit"
                  loading={isLoadingLightheadednessMutation}
                />
              </Form.Item>
            </FooterActions>
          </SurveyFooter>
        </Form>
      </SurveyContent>
    </SurveyPage>
  );
};
